import React, { useState, useEffect } from "react";
import {
  User,
  Shield,
  CreditCard,
  Globe,
  CheckCircle,
  Loader2,
  AlertCircle,
  ShieldCheck,
  Receipt,
  RefreshCw,
  Info,
  ArrowDown,
  Download,
  ArrowLeft,
  ExternalLink,
  Terminal
} from "lucide-react";
import axios from "../config/axios";
import Button from "../components/Button";
import { useSubscription } from "../hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useRecordingTime } from '../hooks/useRecordingTime';
import { paymentService, Transaction } from "../services/paymentService";
import packageJson from "../../package.json";
import { useUserProfile } from '../hooks/useUserProfile';
import DeveloperPortal from './DeveloperPortal';

// Define types for the form data
interface UserProfile {
  name: string;                      // Contact person name
  specialty: string;                 // Business/medical specialty
  role: string;                      // Role in organization
  practice_size: string;             // Organization size
  email: string;
  user_type?: string;                // organization, individual, etc.
  organization_name?: string;        // Company name
  profile_type?: string;             // developer, regular, etc.
}

// Define subscription interfaces matching the ones in useSubscription.ts
interface PlanDetails {
  id: number;
  name: string;
  description: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  price: number;
  currency: string;
  interval: string;
  daily_recording_limit: number | null;
  total_recording_limit: number | null;
  max_recording_duration: number | null;
  is_active: boolean;
  features: Record<string, any>;
}

interface SubscriptionDetails {
  id: number;
  plan: PlanDetails;
  status: string;
  start_date: string;
  end_date: string | null;
  next_renewal_date?: string | null;
  is_trial: boolean;
  cancel_at_period_end: boolean;
}

export interface NavigationItem {
  title: string;
  icon: React.ReactNode;
  id: string;
}

// Create simple Badge component as a fallback
const Badge = ({ variant, children, className = '' }: { variant: string, children: React.ReactNode, className?: string }) => (
  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
    variant === 'default' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
  } ${className}`}>
    {children}
  </span>
);

// Create simple Skeleton component as a fallback
const Skeleton = ({ className }: { className: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

// Create simple Separator component as a fallback
const Separator = () => (
  <div className="h-px w-full bg-gray-200 my-4"></div>
);

const SUPPORT_EMAIL = 'support@scribedo.com';

const Settings: React.FC = () => {
  const { isDeveloper } = useUserProfile();
  const [profile, setProfile] = useState<UserProfile>({
    name: "",
    specialty: "",
    role: "",
    practice_size: "",
    email: "",
    organization_name: "",
    profile_type: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});
  const [activeTab, setActiveTab] = useState("profile");
  const [isSuccessFading, setIsSuccessFading] = useState(false);
  const [isErrorFading, setIsErrorFading] = useState(false);
  const { isLoading: isSubscriptionLoading, subscriptionDetails, refreshSubscription } = useSubscription();
  const [isRefreshingSubscription, setIsRefreshingSubscription] = useState(false);
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState<string>('profile');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const { data: recordingTimeData, isLoading: isRecordingTimeLoading } = useRecordingTime();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [isRefreshingTransactions, setIsRefreshingTransactions] = useState(false);
  const [sortBy, setSortBy] = useState<keyof Transaction>("created");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeveloperPortal, setShowDeveloperPortal] = useState(false);

  // Form validation function
  const validateForm = (): boolean => {
    const newFieldErrors: {[key: string]: boolean} = {};
    let isValid = true;

    // Validate name
    if (!profile.name.trim()) {
      newFieldErrors.name = true;
      setError("Full Name is required.");
      isValid = false;
    }

    // Validate organization fields for developers
    if (isDeveloper) {
      if (!profile.organization_name?.trim()) {
        newFieldErrors.organization_name = true;
        setError("Organization Name is required for developer profiles.");
        isValid = false;
      }
    }

    setFieldErrors(newFieldErrors);
    return isValid;
  };

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let fadeTimer: NodeJS.Timeout;

    if (saveSuccess) {
      timer = setTimeout(() => {
        setIsSuccessFading(true);

        fadeTimer = setTimeout(() => {
          setSaveSuccess(false);
          setIsSuccessFading(false);
        }, 300); // Duration of fade animation
      }, 4700); // Start fading slightly before the 5 second mark
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [saveSuccess]);

  // Auto-dismiss error message after 8 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let fadeTimer: NodeJS.Timeout;

    if (error) {
      timer = setTimeout(() => {
        setIsErrorFading(true);

        fadeTimer = setTimeout(() => {
          setError(null);
          setIsErrorFading(false);
        }, 300); // Duration of fade animation
      }, 7700); // Start fading slightly before the 8 second mark
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [error]);

  // Specialty options
  const specialties = [
    "Family Medicine",
    "Internal Medicine",
    "Pediatrics",
    "Psychiatry",
    "Mental Health Counseling",
    "Cardiology",
    "Dermatology",
    "Endocrinology",
    "Gastroenterology",
    "Neurology",
    "Obstetrics & Gynecology",
    "Oncology",
    "Orthopedics",
    "Pulmonology",
    "Rheumatology",
    "Surgery",
    "Urology",
    "Other",
  ];

  // Role options
  const roles = [
    "Physician",
    "Nurse Practitioner",
    "Physician Assistant",
    "Registered Nurse",
    "Medical Assistant",
    "Other",
  ];

  // Practice size options
  const practiceSizes = [
    "Just me (solo practice)",
    "2 to 9",
    "10 to 49",
    "50 or more",
  ];

  // Navigation items
  const navigationItems: NavigationItem[] = [
    {
      title: 'Profile',
      icon: <User className="h-4 w-4" />,
      id: 'profile',
    },
    {
      title: 'Account',
      icon: <ShieldCheck className="h-4 w-4" />,
      id: 'account',
    },
    {
      title: 'Subscription',
      icon: <CreditCard className="h-4 w-4" />,
      id: 'subscription',
    },
    {
      title: 'Transactions',
      icon: <Receipt className="h-4 w-4" />,
      id: 'transactions',
    },
    {
      title: 'Appearance',
      icon: <Globe className="h-4 w-4" />,
      id: 'appearance',
    },
  ];

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get("/api/scribing/user-profile/me");
        if (response.status === 200) {
          setProfile({
            name: response.data.name || "",
            specialty: response.data.specialty || "",
            role: response.data.role || "",
            practice_size: response.data.practice_size || "",
            email: response.data.email || "",
            organization_name: response.data.organization_name || "",
            profile_type: response.data.profile_type || "",
          });
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear field error when user edits the field
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
    
    // Reset success message when form is modified
    setSaveSuccess(false);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSaveSuccess(false);
    setFieldErrors({});

    // Validate form
    if (!validateForm()) {
      setIsSaving(false);
      return;
    }

    try {
      // Build the request payload
      const requestData: any = {
        name: profile.name.trim(),
        specialty: profile.specialty,
        role: profile.role,
        practice_size: profile.practice_size,
      };

      // Add organization fields for developer profiles
      if (isDeveloper) {
        if (profile.organization_name) {
          requestData.organization_name = profile.organization_name.trim();
        }
      }

      console.log('Updating profile with data:', requestData);

      const response = await axios.put("/api/scribing/user-profile/me", requestData);

      if (response.status === 200) {
        setSaveSuccess(true);
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle subscription refresh
  const handleRefreshSubscription = async () => {
    setIsRefreshingSubscription(true);
    await refreshSubscription();
    setTimeout(() => {
      setIsRefreshingSubscription(false);
    }, 1000);
  };

  // Handle subscription cancellation
  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      setShowCancelModal(false);
      const response = await axios.post('/api/scribing/subscriptions/cancel-async');
      
      if (response.data.status === 'success') {
        // Refresh subscription details to update UI
        await refreshSubscription();
        setSaveSuccess(true);
        setError(null);
        // Show specific success message for cancellation
        console.log('Subscription successfully scheduled for cancellation');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError('Failed to cancel subscription. Please try again later.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle subscription reactivation
  const handleReactivateSubscription = async () => {
    try {
      setIsReactivating(true);
      const response = await axios.post('/api/scribing/subscriptions/reactivate-async');
      
      if (response.data.status === 'success') {
        // Refresh subscription details to update UI
        await refreshSubscription();
        setSaveSuccess(true);
        setError(null);
      }
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      setError('Failed to reactivate subscription. Please try again later.');
    } finally {
      setIsReactivating(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    try {
      setIsTransactionsLoading(true);
      setTransactionsError(null);
      const data = await paymentService.getTransactions();
      setTransactions(data);
    } catch (err: any) {
      setTransactionsError(err.message || "Failed to load transaction history");
    } finally {
      setIsTransactionsLoading(false);
    }
  };
  
  // Load transactions when subscription tab or transactions tab is active
  useEffect(() => {
    if (activeNav === 'subscription' || activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeNav, activeTab]);
  
  // Handle refresh transactions
  const handleRefreshTransactions = async () => {
    if (isRefreshingTransactions) return;
    
    setIsRefreshingTransactions(true);
    try {
      await fetchTransactions();
      // Add a slight delay to show the spinner
      setTimeout(() => {
        setIsRefreshingTransactions(false);
      }, 1000);
    } catch (error) {
      setIsRefreshingTransactions(false);
    }
  };
  
  // Handle sort
  const handleSort = (column: keyof Transaction) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortOrder("desc");
    }
  };
  
  // Format currency display
  const formatCurrency = (amount: number | undefined, currency: string) => {
    if (amount === undefined) return "-";
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase() || 'USD',
    });
    
    return formatter.format(amount);
  };
  
  // Get transaction type display text
  const getTransactionTypeDisplay = (transaction: Transaction) => {
    if (transaction.type === "invoice") {
      return "Invoice";
    } else if (transaction.type === "payment_intent") {
      return "Payment";
    }
    return transaction.type;
  };
  
  // Get transaction status badge
  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
      case "succeeded":
        return <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">Paid</span>;
      case "pending":
        return <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">Pending</span>;
      case "failed":
        return <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">Failed</span>;
      case "canceled":
        return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">Canceled</span>;
      default:
        return <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">{status}</span>;
    }
  };
  
  const SortIndicator = ({ column }: { column: keyof Transaction }) => {
    if (sortBy !== column) return null;
    
    return (
      <ArrowDown 
        className={`inline-block ml-1 h-4 w-4 ${sortOrder === "asc" ? "transform rotate-180" : ""}`} 
      />
    );
  };
  
  // Filter transactions to only show invoices
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter(transaction => transaction.type === "invoice");
  }, [transactions]);

  // Sort transactions based on current sort settings
  const sortedTransactions = React.useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      // Handle dates
      if (sortBy === "created") {
        const dateA = new Date(a[sortBy] || "");
        const dateB = new Date(b[sortBy] || "");
        return sortOrder === "asc" 
          ? dateA.getTime() - dateB.getTime() 
          : dateB.getTime() - dateA.getTime();
      }
      
      // Handle amounts
      if (sortBy === "amount" || sortBy === "amount_paid") {
        const valueA = a[sortBy] || 0;
        const valueB = b[sortBy] || 0;
        return sortOrder === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }
      
      // Handle strings
      const valueA = String(a[sortBy] || "").toLowerCase();
      const valueB = String(b[sortBy] || "").toLowerCase();
      return sortOrder === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  }, [filteredTransactions, sortBy, sortOrder]);

  // Function to view invoice in a new tab
  const viewInvoice = async (invoiceId: string) => {
    try {
      setError(null);
      
      // Get the URL from the API
      const url = await paymentService.downloadInvoice(invoiceId);
      console.log("Got invoice URL:", url);
      
      // Open in a new tab
      window.open(url, '_blank');
      
    } catch (err: any) {
      console.error("Error getting invoice URL:", err);
      setError(err.message || "Failed to open invoice");
    }
  };

  // Email support handler
  const handleEmailSupport = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };

  // Render content based on active tab
  const renderContent = () => {
    switch (activeNav) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  {isDeveloper ? 'Organization Profile' : 'Profile Information'}
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  {isDeveloper
                    ? 'Update your organization information for API integration and developer portal access.'
                    : 'Update your profile information that will be used in your notes and documentation.'
                  }
                </p>
              </div>

              {isLoading ? (
                <div className="p-12 flex justify-center">
                  <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                    <span className="text-gray-500 text-base">
                      Loading profile data...
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl">
                    <div className="space-y-6">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {isDeveloper ? 'Name' : 'Full Name'} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={profile.name}
                          onChange={handleInputChange}
                          className={`block w-full rounded-lg border ${
                            fieldErrors.name 
                              ? "border-red-300 focus:ring-red-500 focus:border-red-500" 
                              : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
                          } shadow-sm py-3 px-4 focus:outline-none focus:ring-2 transition-all duration-200 text-gray-900 text-base`}
                          placeholder="Dr. Jane Smith"
                          required
                        />
                        {fieldErrors.name && (
                          <p className="mt-2 text-sm text-red-600">
                            Full Name is required
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Address
                        </label>
                        <div className="flex">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={profile.email}
                            readOnly
                            className="block w-full rounded-lg border border-gray-200 bg-gray-50 shadow-sm py-3 px-4 text-gray-900 text-base cursor-not-allowed"
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Email address cannot be changed. Contact support for assistance.
                        </p>
                      </div>

                      {/* Organization Fields - Show only for developer profiles */}
                      {isDeveloper && (
                        <>
                          <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                              Organization Information
                            </h3>
                          </div>

                          <div>
                            <label
                              htmlFor="organization_name"
                              className="block text-sm font-medium text-gray-700 mb-2"
                            >
                              Organization Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="organization_name"
                              name="organization_name"
                              value={profile.organization_name || ''}
                              onChange={handleInputChange}
                              className={`block w-full rounded-lg border ${
                                fieldErrors.organization_name
                                  ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                                  : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
                              } shadow-sm py-3 px-4 focus:outline-none focus:ring-2 transition-all duration-200 text-gray-900 text-base`}
                              placeholder="Acme Healthcare"
                              required
                            />
                            {fieldErrors.organization_name && (
                              <p className="mt-2 text-sm text-red-600">
                                Organization Name is required
                              </p>
                            )}
                          </div>

                        </>
                      )}

                      <div>
                        <label
                          htmlFor="specialty"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {isDeveloper ? 'Business/Medical Specialty' : 'Specialty'}
                        </label>
                        <select
                          id="specialty"
                          name="specialty"
                          value={profile.specialty}
                          onChange={handleInputChange}
                          className="block w-full rounded-lg border border-gray-200 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 text-base appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%23666%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_1rem_center] bg-no-repeat"
                        >
                          <option value="" disabled>
                            Select your specialty
                          </option>
                          {specialties.map((specialty) => (
                            <option key={specialty} value={specialty}>
                              {specialty}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="role"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {isDeveloper ? 'Role in Organization' : 'Role'}
                        </label>
                        <select
                          id="role"
                          name="role"
                          value={profile.role}
                          onChange={handleInputChange}
                          className="block w-full rounded-lg border border-gray-200 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 text-base appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%23666%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_1rem_center] bg-no-repeat"
                        >
                          <option value="" disabled>
                            Select your role
                          </option>
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label
                          htmlFor="practice_size"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          {isDeveloper ? 'Organization Size' : 'Practice Size'}
                        </label>
                        <select
                          id="practice_size"
                          name="practice_size"
                          value={profile.practice_size}
                          onChange={handleInputChange}
                          className="block w-full rounded-lg border border-gray-200 shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 text-base appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%23666%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22M6%208l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_1rem_center] bg-no-repeat"
                        >
                          <option value="" disabled>
                            Select practice size
                          </option>
                          {practiceSizes.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        variant="primary"
                        size="md"
                        disabled={isSaving}
                        isLoading={isSaving}
                        className="rounded-lg"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        );
      case 'account':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  Account Settings
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  Manage your account settings and preferences.
                </p>
              </div>
              <div className="p-12 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Shield className="h-10 w-10 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 tracking-tight">
                  Coming Soon
                </h3>
                <p className="text-gray-500 max-w-md text-base font-normal">
                  Account settings will be implemented in a future update. Here you
                  will be able to change your email, password, and manage your
                  subscription.
                </p>
              </div>
            </div>
          </div>
        );
      case 'subscription':
        return (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  Subscription Management
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  View and manage your current subscription plan
                </p>
              </div>

              {isSubscriptionLoading ? (
                <div className="p-12 flex justify-center">
                  <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                    <span className="text-gray-500 text-base">
                      Loading subscription details...
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-8">
                  {!subscriptionDetails ? (
                    // Fallback UI when subscription details is null
                    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                      <div className="flex flex-col items-center justify-center text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <AlertCircle className="h-8 w-8 text-gray-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3 tracking-tight">
                          No Subscription Data Available
                        </h3>
                        <p className="text-gray-500 max-w-md text-base font-normal mb-6">
                          We couldn't retrieve your subscription information. This could be due to a temporary issue.
                        </p>
                        <Button
                          variant="secondary"
                          onClick={handleRefreshSubscription}
                          disabled={isRefreshingSubscription}
                          className="flex items-center gap-2"
                        >
                          {isRefreshingSubscription ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                          Refresh Subscription Data
                        </Button>
                      </div>
                    </div>
                  ) : subscriptionDetails?.plan?.price > 0 || (subscriptionDetails?.status && subscriptionDetails?.status === 'active') ? (
                    // Paid or active plan UI
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Current Plan Card */}
                      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Current Plan
                            </h3>
                            <p className="text-sm text-gray-500">
                              {subscriptionDetails?.plan?.name || 'Unknown Plan'}
                            </p>
                          </div>
                          <Badge variant={subscriptionDetails?.status === 'active' ? 'default' : 'outline'}>
                            {subscriptionDetails?.status === 'active' ? 'Active' : 
                              subscriptionDetails?.status === 'canceled' ? 'Canceled' :
                              subscriptionDetails?.status || 'Unknown'}
                          </Badge>
                        </div>

                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">Started on</span>
                            <span className="text-sm font-medium">{formatDate(subscriptionDetails?.start_date)}</span>
                          </div>
                          
                          {subscriptionDetails?.status === 'active' && 'next_renewal_date' in (subscriptionDetails || {}) && subscriptionDetails?.next_renewal_date && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Renews on</span>
                              <span className="text-sm font-medium">{formatDate(subscriptionDetails.next_renewal_date as string)}</span>
                            </div>
                          )}
                          
                          {subscriptionDetails?.status !== 'active' && subscriptionDetails?.end_date && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Ends on</span>
                              <span className="text-sm font-medium">{formatDate(subscriptionDetails.end_date)}</span>
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">Price</span>
                            <span className="text-sm font-medium">${subscriptionDetails?.plan?.price || 0}/{subscriptionDetails?.plan?.interval || 'month'}</span>
                          </div>
                        </div>

                        <div className="pt-4 space-y-3">
                          {subscriptionDetails && !subscriptionDetails.cancel_at_period_end && 
                            subscriptionDetails?.status !== 'canceled' && 
                            subscriptionDetails?.status !== 'canceled_future_date' && (
                            <Button
                              variant="primary"
                              onClick={() => navigate('/pricing')}
                              className="w-full"
                            >
                              Change Plan
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            onClick={() => refreshSubscription()}
                            className="w-full"
                          >
                            Refresh Details
                          </Button>
                        </div>
                      </div>

                      {/* Plan Features Card */}
                      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Plan Features
                          </h3>
                          <p className="text-sm text-gray-500">
                            Features included in your current plan
                          </p>
                        </div>

                        <div className="space-y-4">
                          {isRecordingTimeLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                            </div>
                          ) : recordingTimeData && recordingTimeData.recording_counts && recordingTimeData.time_usage ? (
                            <>
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-500">Daily Usage</span>
                                  <span className="text-sm font-medium">
                                    {recordingTimeData.recording_counts.daily_count} / {recordingTimeData.limits.daily_limit === null ? '∞' : recordingTimeData.limits.daily_limit === 0 ? '0' : recordingTimeData.limits.daily_limit} recordings
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-gray-500">Total Usage</span>
                                  <span className="text-sm font-medium">
                                    {recordingTimeData.recording_counts.total_count} / {recordingTimeData.limits.total_limit === null ? '∞' : recordingTimeData.limits.total_limit === 0 ? '0' : recordingTimeData.limits.total_limit} recordings
                                  </span>
                                </div>
                              </div>

                              <Separator />

                              <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                  <span className="text-sm text-gray-700">
                                    {recordingTimeData.limits?.daily_limit === null ? 'Unlimited' : recordingTimeData.limits?.daily_limit === 0 ? 'No recordings allowed' : `${recordingTimeData.limits?.daily_limit}`} recordings per day
                                  </span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                  <span className="text-sm text-gray-700">
                                    {recordingTimeData.limits?.total_limit === null ? 'Unlimited' : recordingTimeData.limits?.total_limit === 0 ? 'No recordings allowed' : `${recordingTimeData.limits?.total_limit}`} total recordings
                                  </span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <CheckCircle className="h-5 w-5 text-green-500" />
                                  <span className="text-sm text-gray-700">
                                    {recordingTimeData.limits?.max_duration === null ? 'Unlimited' : recordingTimeData.limits?.max_duration === 0 ? 'No recording allowed' : `Up to ${recordingTimeData.limits?.max_duration}`} minutes per recording
                                  </span>
                                </div>
                              </div>

                              {!recordingTimeData.limits?.unlimited && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                                  <div className="flex items-start">
                                    <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                                    <div className="ml-3">
                                      <h4 className="text-sm font-medium text-blue-800">Usage Limits</h4>
                                      <p className="mt-1 text-sm text-blue-700">
                                        You have usage limits on your current plan. Upgrade to unlock unlimited recordings and longer durations.
                                      </p>
                                      <div className="mt-3">
                                        <Button
                                          variant="primary"
                                          onClick={() => navigate('/pricing')}
                                          className="w-full"
                                        >
                                          Upgrade Plan
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-4 text-sm text-gray-500">
                              Unable to load usage data
                            </div>
                          )}
                        </div>

                        {subscriptionDetails && subscriptionDetails.cancel_at_period_end && subscriptionDetails.status === 'active' ? (
                          <div className="mt-6 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                            <div className="flex items-start">
                              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                              <div className="ml-3">
                                <h4 className="text-sm font-medium text-amber-800">Subscription Cancellation Scheduled</h4>
                                <p className="mt-1 text-sm text-amber-700">
                                  Your subscription is set to cancel at the end of the current billing period.
                                </p>
                                <div className="mt-3">
                                  <Button
                                    variant="secondary"
                                    onClick={handleReactivateSubscription}
                                    disabled={isReactivating}
                                    className="w-full bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                                  >
                                    {isReactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : subscriptionDetails && subscriptionDetails.status === 'canceled_future_date' ? (
                          <div className="mt-6 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                            <div className="flex items-start">
                              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                              <div className="ml-3">
                                <h4 className="text-sm font-medium text-amber-800">Subscription Cancellation Scheduled</h4>
                                <p className="mt-1 text-sm text-amber-700">
                                  Your subscription has been canceled but will remain active until the end of the billing period.
                                </p>
                                <div className="mt-3">
                                  <Button
                                    variant="secondary"
                                    onClick={handleReactivateSubscription}
                                    disabled={isReactivating}
                                    className="w-full bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                                  >
                                    {isReactivating ? 'Reactivating...' : 'Reactivate Subscription'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : subscriptionDetails && (subscriptionDetails.status.toLowerCase() === 'active' || subscriptionDetails.status.toLowerCase() === 'trial') && !subscriptionDetails.cancel_at_period_end ? (
                          <Button
                            variant="danger"
                            onClick={handleCancelSubscription}
                            disabled={isCancelling}
                            className="w-full mt-4"
                          >
                            {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
                          </Button>
                        ) : (subscriptionDetails && subscriptionDetails.status === 'canceled') ? (
                          <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                            <div className="flex items-start">
                              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                              <div className="ml-3">
                                <h4 className="text-sm font-medium text-blue-800">Subscription Canceled</h4>
                                <p className="mt-1 text-sm text-blue-700">
                                  Your subscription has been canceled. Upgrade to a new plan to continue using premium features.
                                </p>
                                <div className="mt-3">
                                  <Button
                                    variant="primary"
                                    onClick={() => navigate('/pricing')}
                                    className="w-full"
                                  >
                                    View Available Plans
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    // Free plan UI
                    <div className="space-y-8">
                      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
                        <div className="flex flex-col items-center justify-center text-center py-8">
                          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                            <CreditCard className="h-8 w-8 text-blue-500" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3 tracking-tight">
                            Free Plan
                          </h3>
                          <p className="text-gray-500 max-w-md text-base font-normal mb-6">
                            You're currently on the free plan. Upgrade to unlock premium features and additional recording time.
                          </p>
                          <Button
                            variant="primary"
                            onClick={() => navigate('/pricing')}
                            className="px-6"
                          >
                            Upgrade to Premium
                          </Button>
                        </div>
                      </div>
                      
                      {/* Display recording time usage */}
                      {recordingTimeData && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900">Usage Statistics</h3>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Daily Usage</span>
                                <span className="text-sm font-medium">
                                  {recordingTimeData.recording_counts.daily_count} / {recordingTimeData.limits.daily_limit === null ? '∞' : recordingTimeData.limits.daily_limit === 0 ? '0' : recordingTimeData.limits.daily_limit} recordings
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Total Usage</span>
                                <span className="text-sm font-medium">
                                  {recordingTimeData.recording_counts.total_count} / {recordingTimeData.limits.total_limit === null ? '∞' : recordingTimeData.limits.total_limit === 0 ? '0' : recordingTimeData.limits.total_limit} recordings
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Recording time</span>
                                <span className="text-sm font-medium">
                                  {recordingTimeData.time_usage.total_minutes.toFixed(1)} mins
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      case 'transactions':
        return (
          <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  Transaction History
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  View and manage your billing and payment history
                </p>
              </div>

              <div className="px-8 py-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Invoices & Payments</h3>
                  <Button
                    variant="secondary"
                    onClick={handleRefreshTransactions}
                    disabled={isRefreshingTransactions}
                    className="flex items-center gap-2"
                  >
                    {isRefreshingTransactions ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Refresh
                  </Button>
                </div>
              </div>

              {isTransactionsLoading ? (
                <div className="px-8 py-12 flex justify-center">
                  <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                    <span className="text-gray-500 text-sm">
                      Loading transaction history...
                    </span>
                  </div>
                </div>
              ) : transactionsError ? (
                <div className="px-8 py-6">
                  <div className="flex items-center p-4 bg-red-50 rounded-lg border border-red-100">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-600">{transactionsError}</p>
                  </div>
                </div>
              ) : sortedTransactions.length === 0 ? (
                <div className="px-8 py-12 flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    <Receipt className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-base font-medium text-gray-900 mb-1">No transactions yet</h3>
                  <p className="text-sm text-gray-500 max-w-sm">
                    Your transaction history will appear here once you make a purchase or payment.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-t border-gray-100">
                      <tr>
                        <th 
                          className="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("created")}
                        >
                          <div className="flex items-center">
                            Date
                            <SortIndicator column="created" />
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("amount")}
                        >
                          <div className="flex items-center">
                            Amount
                            <SortIndicator column="amount" />
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {sortedTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-8 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatDate(transaction.created)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getTransactionTypeDisplay(transaction)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(transaction.status)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {transaction.type === "invoice" && (
                              <button
                                onClick={() => viewInvoice(transaction.id)}
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 ml-auto"
                              >
                                <Download className="h-4 w-4" />
                                Invoice
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      case 'appearance':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  Appearance Settings
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  Customize the appearance of your application.
                </p>
              </div>
              <div className="p-12 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Globe className="h-10 w-10 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 tracking-tight">
                  Coming Soon
                </h3>
                <p className="text-gray-500 max-w-md text-base font-normal">
                  Appearance settings will be implemented in a future update. Here you
                  will be able to customize themes and layout preferences.
                </p>
              </div>
            </div>
          </div>
        );
      case 'support':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">
                  Support
                </h2>
                <p className="text-gray-500 mt-2 text-base font-normal">
                  Get help and contact our support team
                </p>
              </div>
              <div className="p-8">
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Support</h3>
                    <p className="text-gray-600 mb-4">
                      Our support team is here to help you with any questions or issues you may have. Use the chat widget or send us an email.
                    </p>
                    <div className="space-y-4">
                      <button
                        onClick={handleEmailSupport}
                        className="w-full sm:w-auto px-4 py-2 bg-[#0078d4] text-white rounded-md hover:bg-[#106ebe] focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:ring-offset-2 transition-colors"
                      >
                        Email Support
                      </button>
                      <p className="text-sm text-gray-500">
                        Email us at: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#0078d4] hover:underline">{SUPPORT_EMAIL}</a>
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Help Center</h3>
                    <p className="text-gray-600 mb-4">
                      Check out our help center for detailed guides and documentation.
                    </p>
                    <a
                      href="https://help.scribedo.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#0078d4] hover:underline"
                    >
                      Visit Help Center
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen bg-[#F5F5F7]">
      {/* Left Sidebar */}
      <div className="w-[300px] bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          </div>
          <p className="text-sm text-gray-500">
            Manage your profile and preferences
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveNav(item.id);
                setError(null);
                setFieldErrors({});
                setSaveSuccess(false);
              }}
              className={`w-full p-4 border-b border-gray-200 text-left transition-colors ${
                activeNav === item.id
                  ? "bg-blue-50 border-l-4 border-l-blue-500"
                  : "hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={
                    activeNav === item.id ? "text-blue-600" : "text-gray-600"
                  }
                >
                  {item.icon}
                </span>
                <h2
                  className={`font-medium ${
                    activeNav === item.id ? "text-blue-600" : "text-gray-600"
                  }`}
                >
                  {item.title}
                </h2>
              </div>
              <p className="text-sm text-gray-600 pl-7">
                {item.id === "profile"
                  ? "Update your personal information"
                  : item.id === "developer"
                  ? "API keys, testing, and documentation"
                  : item.id === "account"
                  ? "Manage your account settings"
                  : item.id === "subscription"
                  ? "View and manage your subscription"
                  : item.id === "transactions"
                  ? "View your billing history"
                  : item.id === "appearance"
                  ? "Customize your interface"
                  : "Configure notification preferences"}
              </p>
            </button>
          ))}
        </div>

        <div className="mt-auto border-t border-gray-200 p-4">
          <div className="text-xs text-gray-500">
            <p>MY NOTES v{packageJson.version}</p>
            <p className="mt-1">© 2025 DocuMD, Inc.</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-8">
        {/* Notification Banner */}
        {(saveSuccess || error) && (
          <div className="mb-6 transform transition-all duration-300 animate-slide-in max-w-4xl mx-auto">
            {saveSuccess && (
              <div
                className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-center space-x-3 transition-opacity duration-300 ${
                  isSuccessFading ? "opacity-0" : "opacity-100"
                }`}
              >
                <CheckCircle className="h-5 w-5 text-blue-500" />
                <span className="text-gray-900 text-sm font-medium">
                  Your changes have been saved successfully.
                </span>
              </div>
            )}

            {error && (
              <div
                className={`bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-center space-x-3 transition-opacity duration-300 ${
                  isErrorFading ? "opacity-0" : "opacity-100"
                }`}
              >
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-gray-900 text-sm font-medium">
                  {error}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content Area */}
        <div className="max-w-4xl mx-auto h-full">{renderContent()}</div>
      </div>

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Cancel Subscription</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel your subscription? Your subscription will remain active until {subscriptionDetails?.end_date ? formatDate(subscriptionDetails.end_date) : subscriptionDetails?.next_renewal_date ? formatDate(subscriptionDetails.next_renewal_date) : 'the end of your current billing period'} and will not renew automatically.
            </p>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
                className="px-4 py-2"
              >
                Keep Subscription
              </Button>
              <Button
                variant="danger"
                onClick={confirmCancelSubscription}
                disabled={isCancelling}
                className="px-4 py-2"
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export the component as the default export
export default Settings;
